// Catalog.ID Client — Voucher Reference Card
// ============================================================================
// What a buyer prints for a registration voucher they have bought and not yet
// used (registration-wallets §7, storefront bullet). Its sibling, the invitation
// card, is addressed to ONE person and carries fixed values; a voucher card is
// addressed to nobody — that is the difference between the two, and the reason
// a voucher card can simply be handed over while an invitation cannot be
// re-aimed.
//
// It rides the invitation card's pipeline exactly: credit-card 85.6 × 54 mm,
// ten to an A4 sheet in a 2 × 5 grid, crop marks in the page background, no
// letterhead, no named styles, black ink only. Everything geometric is imported
// from `invitation-card.ts` so the trim box is measured once; only the content
// differs.
//
// What is on it, and why:
//
//   the code       in 12 pt on its own full-width line — the one thing on this
//                  card somebody has to read across a desk and type;
//   the QR         `https://<site>/register#code=RGV-…` and nothing else. The
//                  entry page routes on the prefix, so the same link serves a
//                  voucher and an invitation;
//   the bits       what the voucher's lock carries (D1). A voucher is money as
//                  well as a registration, and the buyer needs to see which
//                  voucher is the 100 000-bit one;
//   the type       personal, organization or delegate — the price and the kind
//                  of account differ, and a delegate voucher is redeemed
//                  through an organization's invitation, not on its own;
//   one line       what to do with it: use it, or personalize it to hand it
//                  over (D17 — personalizing is the act of handing over, never
//                  a step for your own ticket).
//
// PAGE BUDGET: cards abut on the sheet, so anything past 153.1 pt prints on top
// of the card below and NOTHING in the unit tests can see it. A voucher card is
// far lighter than a loaded delegate invitation card (no organization, no CID,
// no roles, no fixed values): all four samples measured 128.8–128.9 pt of 153.1
// on a 300 dpi raster (2026-09-14), 24.2 pt clear of the cut, so the invitation
// card's heavy case stays the binding one. Re-render
// `dev/render-booklet.ts <dir> --png` and MEASURE after touching any size here.
//
// ASSET-FREE, like `invitation-card.ts`: no `?raw`/`?url` import may appear in
// anything this pulls in, or the sample renderer can neither run nor typecheck it.

import type { Content, TableCell, TDocumentDefinitions } from 'pdfmake/interfaces';
import { qrToSvg } from '../qr-svg';
import { encodeUtf8 } from '../../crypto/aes';
import { formatVoucherCode, normalizeVoucherCode } from '../../auth/voucher-code';
import {
    buildCardDocument, buildCardSheetDocument, formatBits,
    INVITATION_CARD_QR_ECC,
} from './invitation-card';

/** What the voucher buys. The three registration types, as the storefront and
 *  the server name them. */
export type VoucherCardKind = 'personal' | 'organization' | 'delegate';

/** Title line per kind — somebody handed this card has to know what it is for
 *  before they type anything. */
export const VOUCHER_CARD_TITLES: Record<VoucherCardKind, string> = {
    personal: 'Catalog.ID Registration Voucher',
    organization: 'Catalog.ID Organization Voucher',
    delegate: 'Catalog.ID Delegate Voucher',
};

/** What the row beside the type says the voucher buys. */
const VOUCHER_CARD_FOR: Record<VoucherCardKind, string> = {
    personal: 'A personal Catalog.ID',
    organization: 'An organization identity',
    delegate: 'A delegate seat in an organization',
};

/** The one instruction line. D17 in a sentence: a voucher is used by its buyer
 *  as it is, and personalizing it is what you do to give it to someone else. */
export const VOUCHER_CARD_INSTRUCTION =
    'Use it yourself, or personalize it to hand it over.';

export const VOUCHER_SHEET_TITLE = 'Catalog.ID Voucher Cards';

// ── sizes (explicit, like the invitation card: no named styles) ─────────────

const TITLE_SIZE = 8.5;
const LABEL_SIZE = 6.5;
const VALUE_SIZE = 7;
const URL_SIZE = 6;
/** The code gets the biggest type on the card, and its own line. */
const CODE_SIZE = 12;

/** Geometry of the details block beside the QR — the invitation card's numbers,
 *  repeated here rather than imported, because they are the layout of THIS
 *  card's two columns and the invitation card's are private to it. */
const CARD_PADDING = 7;
const CARD_INNER_WIDTH = 242.6 - 2 * CARD_PADDING;
const QR_FIT = 74;
const QR_COLUMN_GAP = 8;
const LABEL_COLUMN_WIDTH = 46;
/** Stated, never `'*'`: a star column grows when its content cannot fit and
 *  pushes the QR clean out of the trim box — measured on the invitation card,
 *  2026-09-07, and the same trap applies here. */
const DETAILS_COLUMN_WIDTH = CARD_INNER_WIDTH - QR_FIT - QR_COLUMN_GAP;
const VALUE_COLUMN_WIDTH = DETAILS_COLUMN_WIDTH - LABEL_COLUMN_WIDTH - 4;

const CARD_TABLE_LAYOUT = {
    hLineWidth: (): number => 0,
    vLineWidth: (): number => 0,
    paddingTop: (): number => 0.5,
    paddingBottom: (): number => 0.5,
    paddingLeft: (): number => 0,
    paddingRight: (): number => 4,
};

export interface VoucherCardData {
    /** The voucher code; printed in DISPLAY form and carried by the QR inside
     *  {@link url}. */
    code: string;
    /** What the voucher buys. */
    registrationType: VoucherCardKind;
    /** Bits the voucher's lock carries (D1). Null/absent ⇒ no bits line, which
     *  is waived mode (D9) and nothing else. */
    bits?: number | null;
    /** The entry address the QR carries — `voucherUrl(site, code)`. */
    url: string;
    /** The same address WITHOUT the code, printed as the typeable line. */
    siteUrl?: string;
}

/**
 * The content of ONE voucher card, sized for the shared trim box. Laid out like
 * the invitation card — title, code, details beside the QR, address across the
 * full width — so a buyer holding one of each recognises them as a pair.
 */
export function buildVoucherCard(data: VoucherCardData): Content[] {
    const normalised = normalizeVoucherCode(data.code);
    if (!normalised) throw new Error('That is not a valid voucher code');
    const code = formatVoucherCode(normalised);
    const bits = data.bits ?? null;

    const rows: TableCell[][] = [
        [
            { text: 'For', fontSize: LABEL_SIZE },
            { text: VOUCHER_CARD_FOR[data.registrationType], fontSize: VALUE_SIZE, bold: true },
        ],
    ];

    return [
        // The bits ride the TITLE row, right-aligned — the same place the
        // invitation card puts them, and for the same measured reason (see
        // `cardTitleRow` there). A voucher card is light enough not to need the
        // space; it shares the treatment so the pair reads as a pair.
        bits === null
            ? {
                text: VOUCHER_CARD_TITLES[data.registrationType],
                fontSize: TITLE_SIZE, bold: true, margin: [0, 0, 0, 2],
            }
            : {
                columns: [
                    {
                        text: VOUCHER_CARD_TITLES[data.registrationType],
                        fontSize: TITLE_SIZE, bold: true,
                    },
                    {
                        text: `${formatBits(bits)} bits`,
                        fontSize: VALUE_SIZE, bold: true, alignment: 'right', width: 'auto',
                    },
                ],
                columnGap: 4,
                margin: [0, 0, 0, 2],
            },
        // Full width and its own line: beside the QR the code wrapped after the
        // third group, which is the one thing here that must be easy to read back.
        { text: code, fontSize: CODE_SIZE, bold: true, margin: [0, 0, 0, 3] },
        {
            columns: [
                {
                    width: DETAILS_COLUMN_WIDTH,
                    layout: CARD_TABLE_LAYOUT,
                    table: { widths: [LABEL_COLUMN_WIDTH, VALUE_COLUMN_WIDTH], body: rows },
                },
                {
                    width: QR_FIT,
                    svg: qrToSvg(encodeUtf8(data.url), { ecc: INVITATION_CARD_QR_ECC }),
                    fit: [QR_FIT, QR_FIT],
                },
            ],
            columnGap: QR_COLUMN_GAP,
            margin: [0, 0, 0, 3],
        },
        { text: data.siteUrl ?? data.url, fontSize: URL_SIZE, margin: [0, 0, 0, 2] },
        { text: VOUCHER_CARD_INSTRUCTION, fontSize: URL_SIZE },
    ];
}

/** The print job: up to ten voucher cards per A4 sheet, cut apart along the
 *  crop marks. Pass it to `printPdf()` — it carries its own background. */
export function buildVoucherCardSheet(cards: readonly VoucherCardData[]): TDocumentDefinitions {
    return buildCardSheetDocument(cards, buildVoucherCard, VOUCHER_SHEET_TITLE,
        'There are no voucher cards to print');
}

/** One card as its own page — what the PNG export rasterises. */
export function buildVoucherCardDocument(card: VoucherCardData): TDocumentDefinitions {
    return buildCardDocument(buildVoucherCard(card), VOUCHER_SHEET_TITLE);
}
